import { FC, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MoCxlGroupData,
  MoDocTableData,
  MoFlightData,
  MoInfoData,
  MoTableDetailsData,
  MoTablePaxesData,
  MoTablePaymentData,
  MoTableRemarksData,
  MoTableRoomData,
  MyOrder,
  OrderLog,
} from 'ts/shared/interfaces/MOResponse';
import {
  getIcon,
  getMoDetailsTable,
  getStatus,
  moCxlGroup,
  moDocTable,
  moFlightData,
  moFlightTable,
  moInfoTable,
  moOwnerTable,
  moParamsTable,
  moPaxesTable,
  moPaymentsTable,
  moRemarksTable,
  moRoomTable,
} from 'constants/mo';
import {
  formatCxlGroup,
  formatDetailsGroup,
  formatDocTable,
  formatFlightTable,
  formatInfoGroup,
  formatPaxesTable,
  formatPaymentTable,
  formatRemarksTable,
  formatRoomTable,
  getMoDetails,
} from 'utils/shared/my-order';
import MoAmount from './MoAmount';
import MoGroupData from './MoGroupData';
import { dateFormat, showError, showSuccess } from 'utils/utils';
import MoActionsRow from './MoActions/MoActionsRow';
import MoSupplier from './MoSupplier';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faEnvelope,
  faFile,
  faInfo,
  faMoneyBill,
  faPlane,
  faTag,
  faTicket,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import MoAddRemark from './MoActions/MoAddRemark';
import MoTicket from './MoTicket';
import Table from './Table';
import MoUploadDocument from './MoActions/MoUploadDocument';
import AddPaymentModal from './AddPaymentModal';
import Button from 'components/ui/Button/Button';
import MoAddPrivateRemark from './MoModal/MoAddPrivateRemark';
import { MainContext } from 'contexts/MainContext';
import Tabs from 'components/ui/Tabs/Tabs';
import { closeRequest, deleteDocument, downloadDocument } from 'services/myOrders';
import { highlightAssistantTargets } from 'utils/assistantHighlight';
import {
  ORDER_PANEL_TAB_ID,
  OrdersAction,
  actionTargetsOrder,
  getOrdersActionsVersion,
  impliedOrderTab,
  peekOrdersActions,
  subscribeOrdersActions,
  useOrdersAction,
} from './Assistant/ordersActions';

interface MoOrderTabs2Props {
  activeOrder: MyOrder;
  onUpdateMyOrder: (my: MyOrder) => void;
  onLoadLogs: (logs: OrderLog[]) => void;
  activeInitialTab?: number;
  showCopyOrderJson?: boolean;
}

const MoOrderTabs2: FC<MoOrderTabs2Props> = ({ activeOrder, onUpdateMyOrder, onLoadLogs, activeInitialTab, showCopyOrderJson }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(4);
  const [internalRemarksData, setInternalRemarksData] = useState<MoTableRemarksData[]>([]);
  const [remarksData, setRemarksData] = useState<MoTableRemarksData[]>([]);
  const [docsData, setDocsData] = useState<MoDocTableData[]>([]);
  const [paymentsTable, setPaymentsTable] = useState<MoTablePaymentData[]>([]);
  const [detailsGroup, setDetailsGroup] = useState<MoTableDetailsData>();
  const [roomsData, setRoomsData] = useState<MoTableRoomData[]>([]);
  const [paxesTable, setPaxesTable] = useState<MoTablePaxesData[]>([]);
  const [flightData, setFlightData] = useState<MoFlightData[]>([]);
  const [cxlData, setCxlData] = useState<MoCxlGroupData>();
  const [infoData, setInfoData] = useState<MoInfoData>();
  const { user } = useContext(MainContext);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const ordersActionVersion = useSyncExternalStore(subscribeOrdersActions, getOrdersActionsVersion);
  const orderTarget = {
    segmentId: activeOrder.orderRow.segmentId,
    altSegmentId: activeOrder.fullSegment.orderSegId,
    orderId: activeOrder.orderRow.orderId,
    altOrderId: activeOrder.fullSegment.orderId,
  };
  const targetsThisOrder = (action: OrdersAction) => actionTargetsOrder(action, orderTarget, true);

  const showOrderTab = (tab: string) => {
    if (tab === 'internal-remarks' && user?.branchId !== 5000) return;
    if (tab === 'ticket' && !activeOrder.order.hp_Ticket) return;
    const id = ORDER_PANEL_TAB_ID[tab];
    if (id == null) return;
    setActiveTab(id);
    highlightAssistantTargets(['orders-tabs']);
  };

  useEffect(() => {
    if (activeInitialTab) {
      setActiveTab(activeInitialTab);
    }
  }, [activeInitialTab]);

  useEffect(() => {
    const pending = peekOrdersActions().find(
      (action) => impliedOrderTab(action.action) && targetsThisOrder(action),
    );
    const tab = pending ? impliedOrderTab(pending.action) : null;
    if (tab) showOrderTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersActionVersion, activeOrder]);

  useOrdersAction(
    (action) => action.action === 'tab' && action.tabScope === 'order' && targetsThisOrder(action),
    (action) => {
      if (action.tab) showOrderTab(action.tab);
    },
  );

  useOrdersAction(
    (action) => action.action === 'copy' && targetsThisOrder(action),
    (action) => {
      showOrderTab('summary');
      const text =
        action.copyField === 'segment'
          ? String(activeOrder.orderRow.segmentId)
          : action.copyField === 'order'
            ? String(activeOrder.orderRow.orderId)
            : activeOrder.fullSegment.itemDesc || '';
      if (!text) return;
      void navigator.clipboard.writeText(text);
      showSuccess(t('mo.clipboard_success'));
    },
  );

  useOrdersAction(
    (action) => action.action === 'add-payment' && targetsThisOrder(action),
    () => {
      showOrderTab('payments');
      if (user?.branchId === 5000) setShowAddPaymentModal(true);
    },
  );

  useOrdersAction(
    (action) =>
      targetsThisOrder(action) &&
      ((action.action === 'add-internal-remark' && user?.branchId !== 5000) ||
        (action.action === 'ticket-reply' && !activeOrder.order.hp_Ticket)),
    () => undefined,
  );

  useOrdersAction(
    (action) => action.action === 'close-remark' && targetsThisOrder(action),
    (action) => {
      const closable = ['APPART', 'APPOPN', 'INTOP'];
      const pools = [
        ...(activeOrder.fullSegment.remarks || []),
        ...(user?.branchId === 5000 ? activeOrder.fullSegment.internalRemarks || [] : []),
      ];
      const remark = pools.find((item) => {
        if (action.remarkId && Number(item.orderSegRemarkId) === action.remarkId) return true;
        const desc = String(item.remarkDesc || '');
        if (desc.includes('Closed')) return false;
        if (action.remarkCode && item.remarkCode !== action.remarkCode) return false;
        if (action.message && !desc.toLowerCase().includes(action.message.toLowerCase())) return false;
        return Boolean(action.remarkCode || action.message);
      });
      if (!remark || (remark.remarkCode && !closable.includes(remark.remarkCode))) return;
      const isInternal = (activeOrder.fullSegment.internalRemarks || []).includes(remark);
      showOrderTab(isInternal ? 'internal-remarks' : 'remarks');
      void closeRequest({
        orderId: activeOrder.fullSegment.orderId,
        segmentId: activeOrder.fullSegment.orderSegId,
        remarkId: String(remark.orderSegRemarkId),
      }).then((res) => {
        if (res?.myOrder) onUpdateMyOrder(res.myOrder);
      });
    },
  );

  useOrdersAction(
    (action) =>
      (action.action === 'download-document' || action.action === 'delete-document') &&
      targetsThisOrder(action),
    (action) => {
      showOrderTab('documents');
      const document = activeOrder.order.documents.find((item) => {
        if (action.documentId && item.orderDocumentId === action.documentId) return true;
        if (
          action.documentName &&
          item.documentName?.toLowerCase().includes(action.documentName.toLowerCase())
        ) {
          return true;
        }
        return false;
      });
      if (!document) return;
      if (action.action === 'download-document') {
        void downloadDocument(
          {
            orderId: document.orderId,
            segId: document.orderSegId,
            documentId: document.orderDocumentId,
          },
          document.documentName,
        );
        return;
      }
      if (!action.confirm || document.documentName.includes('Reservations screenshot')) return;
      void deleteDocument({
        orderId: document.orderId,
        segId: document.orderSegId,
        documentId: document.orderDocumentId,
      }).then((response) => {
        if (response?.deleted) {
          showSuccess(t('mo.document_deleted'));
          onUpdateMyOrder({
            ...activeOrder,
            order: {
              ...activeOrder.order,
              documents: activeOrder.order.documents.filter(
                (item) => item.orderDocumentId !== document.orderDocumentId,
              ),
            },
          });
        } else {
          showError(t('something_went_wrong'));
        }
      });
    },
  );

  useEffect(() => {
    const allRemarks = formatRemarksTable(activeOrder.fullSegment, activeOrder.fullSegment.remarks, onUpdateMyOrder);
    const internalRemarks = formatRemarksTable(activeOrder.fullSegment, activeOrder.fullSegment.internalRemarks, onUpdateMyOrder);
    setRemarksData(allRemarks.filter((remark) => remark.remarkType !== 'INTOP'));
    setInternalRemarksData(internalRemarks);
    const onDocumentDeleted = (documentId: number) => {
      setDocsData((prev) => prev.filter((doc) => doc.documentId !== documentId));
      onUpdateMyOrder({
        ...activeOrder,
        order: {
          ...activeOrder.order,
          documents: activeOrder.order.documents.filter(
            (doc) => doc.orderDocumentId !== documentId
          ),
        },
      });
    };
    setDocsData(
      formatDocTable(activeOrder.order.documents, activeOrder.orderRow.companyId, onDocumentDeleted)
    );
    setPaymentsTable(formatPaymentTable(activeOrder.order.paymentTransactionResponses));
    setDetailsGroup(formatDetailsGroup(activeOrder));
    setRoomsData(formatRoomTable(activeOrder.fullSegment));
    setPaxesTable(formatPaxesTable(activeOrder.order.paxes));
    setFlightData(formatFlightTable(activeOrder.fullSegment));
    setCxlData(formatCxlGroup(activeOrder.fullSegment));
    setInfoData(formatInfoGroup(activeOrder));
  }, [activeOrder]); // eslint-disable-line

  return (
    <div className="mo-tabs2" data-assistant-target="orders-tabs">
      <Tabs
        size="small"
        label={t('mo.segment_full')}
        value={String(activeTab)}
        onValueChange={(v) => setActiveTab(Number(v))}
        className="text-sm"
        items={[
          {
            value: '4',
            label: <FontAwesomeIcon icon={getIcon(activeOrder.fullSegment.productType)} />,
          },
          ...(activeOrder.fullSegment.rooms.length
            ? [
                { value: '5', label: t('hotel.rooms') },
                {
                  value: '8',
                  label: (
                    <>
                      CXL -{' '}
                      <span
                        className={
                          new Date(activeOrder.fullSegment.rooms[0].cancellationDateLimit) > new Date()
                            ? 'text-success'
                            : 'color-danger'
                        }
                      >
                        {dateFormat(activeOrder.fullSegment.rooms[0].cancellationDateLimit, 'dd-MM-yyyy')}
                      </span>
                    </>
                  ),
                },
              ]
            : []),
          ...(activeOrder.fullSegment.details.length
            ? [
                {
                  value: '5',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faInfo} />
                      Details
                    </>
                  ),
                },
              ]
            : []),
          {
            value: '9',
            label: (
              <>
                <FontAwesomeIcon icon={faTag} />
                Supplier
                {user?.branch?.hotelDisplaySupplierName && (
                  <span className="text-success">
                    {' '}-{' '}
                    <span style={{ textTransform: 'capitalize' }}>
                      {activeOrder.fullSegment.sysSuppDesc?.toLowerCase()?.split(' ')?.[0]}
                    </span>
                  </span>
                )}
              </>
            ),
          },
          {
            value: '6',
            label: (
              <>
                <FontAwesomeIcon icon={faMoneyBill} />
                {t('mo.amount')}
              </>
            ),
          },
          {
            value: '0',
            label: (
              <>
                <FontAwesomeIcon icon={faEnvelope} />
                {t('mo.remarks')} ({remarksData.length})
              </>
            ),
          },
          ...(user?.branchId === 5000
            ? [
                {
                  value: '11',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faEnvelope} />
                      Internal Remarks ({internalRemarksData.length})
                    </>
                  ),
                },
              ]
            : []),
          {
            value: '7',
            label: (
              <>
                <FontAwesomeIcon icon={faUsers} />
                Paxes
              </>
            ),
          },
          {
            value: '3',
            label: (
              <>
                <FontAwesomeIcon icon={faCcVisa} />
                {t('mo.payments')} ({paymentsTable.length})
              </>
            ),
          },
          {
            value: '1',
            label: (
              <>
                <FontAwesomeIcon icon={faUser} />
                {t('mo.info')}
              </>
            ),
          },
          {
            value: '2',
            label: (
              <>
                <FontAwesomeIcon icon={faFile} />
                {t('mo.documents')} ({docsData.length})
              </>
            ),
          },
          ...(activeOrder.order.hp_Ticket
            ? [
                {
                  value: '10',
                  label: (
                    <>
                      <FontAwesomeIcon icon={faTicket} />
                      {t('mo.ticket')} <span className="text-contrast">{activeOrder.order.hp_Ticket}</span>
                    </>
                  ),
                },
              ]
            : []),
        ]}
      />
      <div className="mo-tabs-content-wr">
        <div className="text-bold mb-5 text-st mo-tabs-title">
          {t('mo.segment_full')}-{activeOrder.orderRow?.segmentId} ({t('mo.order')}-
          {activeOrder.orderRow?.orderId})
          {activeTab === 2 && (
            <div className="mt-10">
              <MoUploadDocument myOrder={activeOrder} onSuccess={onUpdateMyOrder} />
            </div>
          )}
        </div>
        <div className="mo-tabs-content">
          {activeTab === 4 && !!detailsGroup && (
            <div>
              <div className="mo-tabs-row mb-10 text-black">
                <div className="text-bold mb-5">{activeOrder.fullSegment.itemDesc}
                  <FontAwesomeIcon
                    icon={faCopy}
                    size="xs"
                    color="var(--color-warning)"
                    className="ml-5 cur-poi"
                    onClick={() => {
                      navigator.clipboard.writeText(`${activeOrder.fullSegment.itemDesc}`);
                      showSuccess(t('mo.clipboard_success'));
                    }}
                  /></div>
              </div>
              <MoGroupData
                mainClassName="mo-group-wr-3x"
                data={detailsGroup}
                columns={getMoDetailsTable(activeOrder.fullSegment.productType)}
                columnsCount={3}
                columnWidth={110}
              />
            </div>
          )}
          {activeTab === 5 && !!roomsData.length && (
            <Table data={roomsData} columns={moRoomTable} />
          )}
          {activeTab === 5 && !!flightData.length && (
            <div>
              <Table data={flightData} columns={moFlightTable} />
              <div className="mt-n20"></div>
              {activeOrder.fullSegment.details.map((detail, index) => (
                <div key={index}>
                  <div className="mo-tabs-row mb-10 mt-20 text-bold text-sm display-flex">
                    {detail.productTypeCode === 'FLT' && (
                      <span>
                        #{detail.orderSegDetailId}
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="ph-10"
                          size="sm"
                          transform={{ rotate: -45 }}
                        />
                        <span className="ml-10">{detail.item.name}</span>
                      </span>
                    )}
                    {detail.productTypeCode === 'SACT' && <span>{detail.item.name}</span>}
                    {detail.productTypeCode === 'HTL' && (
                      <div>
                        <div>
                          #{detail.orderSegDetailId}{' '}
                          <span className="ph-10">{detail.item.name}</span>
                        </div>
                        <div>
                          Room: {detail.room.name} [Board: {detail.board.code}]
                        </div>
                        <div>
                          Paxes:{' '}
                          {activeOrder.order.paxes
                            .map((p) => `${p.firstName} ${p.lastName}`)
                            .join(', ')}
                        </div>
                      </div>
                    )}

                    <span className="ml-a">
                      <span className="ph-10">Paxes: {detail.paxes.length}</span>
                      <span className="pl-10">
                        <span className={`mo-status ${getStatus(detail.sysStatus.code)}`}>
                          {detail.sysStatus.code}
                        </span>
                      </span>
                    </span>
                  </div>
                  <MoGroupData
                    mainClassName="mo-group-wr-3x"
                    data={getMoDetails(detail)}
                    columns={moFlightData}
                    columnsCount={3}
                    columnWidth={110}
                  />
                </div>
              ))}
            </div>
          )}
          {activeTab === 6 && <MoAmount segment={activeOrder.fullSegment} />}
          {activeTab === 7 && (
            <div>
              <Table data={paxesTable} columns={moPaxesTable} />
              {activeOrder.fullSegment.productType !== 'FLT' && (
                <MoGroupData
                  mainClassName="mo-group-wr-4x"
                  columns={moOwnerTable}
                  data={activeOrder.order.owner}
                  columnsCount={4}
                  columnWidth={110}
                />
              )}
              <div className="mb-20"></div>
              <Table data={[activeOrder.order]} columns={moParamsTable} />
            </div>
          )}
          {activeTab === 8 && !!roomsData.length && (
            <div
              className={
                new Date(activeOrder.fullSegment.rooms?.[0]?.cancellationDateLimit) > new Date()
                  ? ''
                  : 'mo-group-cxl'
              }
            >
              <div className="flex-between mo-tabs-row mb-20">
                <span className="text-bold">{roomsData[0].room}</span>
                <span>{roomsData[0].paxStr}</span>
                <span className={`mo-status ml-4 ${getStatus(roomsData[0].status)}`}>
                  {roomsData[0].status}
                </span>
              </div>

              <MoGroupData
                columns={moCxlGroup}
                data={cxlData}
                columnsCount={1}
                mainClassName="mo-group-wr-1x"
              />
            </div>
          )}

          {activeTab === 0 && (
            <div>
              <MoAddRemark orderRow={activeOrder.orderRow} onSuccess={onUpdateMyOrder} />
              <Table data={remarksData} columns={moRemarksTable} />
            </div>
          )}

          {activeTab === 11 && (
            <div>
              <MoAddPrivateRemark orderRow={activeOrder.orderRow} onSuccess={onUpdateMyOrder} />
              <Table data={internalRemarksData} columns={moRemarksTable} />
            </div>
          )}
          {activeTab === 1 && (
            <div>
              <div className="mo-tabs-row mb-10">
                <div className="text-bold mb-5">
                  {activeOrder.orderRow.agentName} | {activeOrder.orderRow.branchName}
                </div>
              </div>
              <MoGroupData
                mainClassName="mo-group-wr-2x mo-group-wr-info"
                columns={moInfoTable}
                data={infoData}
                columnsCount={2}
                columnWidth={110}
              />
            </div>
          )}
          {activeTab === 2 && <Table data={docsData} columns={moDocTable} />}
          {activeTab === 3 && <div>
            {
              user?.branchId === 5000 &&
              <Button className='btn btn-primary btn-ssm mb-10' title={t('mo.add_payment')} onClick={() => setShowAddPaymentModal(true)} />
            }
            {!!paymentsTable.length && <Table data={paymentsTable} columns={moPaymentsTable} />}
          </div>
          }
          {activeTab === 9 && (
            <MoSupplier
              myOrder={activeOrder}
              supplier={activeOrder.suppliersByCode[activeOrder.orderRow.sysSuppCode]}
            />
          )}
          {activeTab === 10 && activeOrder.order.hp_Ticket && <MoTicket myOrder={activeOrder} />}
        </div>
      </div>
      <MoActionsRow
        myOrder={activeOrder}
        onUpdateMyOrder={onUpdateMyOrder}
        onLoadLogs={onLoadLogs}
        showCopyOrderJson={showCopyOrderJson}
      />
      <AddPaymentModal isOpen={showAddPaymentModal} onClose={() => setShowAddPaymentModal(false)} order={activeOrder} />

    </div>
  );
};

export default MoOrderTabs2;
